import React, { useState, useEffect } from "react";
import { Box, Typography, Paper, Button } from "@mui/material";
import { motion } from "framer-motion";
import { getServices } from "../services/api/getAll";
import { useTranslation } from "react-i18next";
import BookingForm from "./BookingForm";

// Import premium service images
import serviceTowing from "../assets/service_towing.jpg";
import serviceTire from "../assets/service_tire.jpg";
import serviceBattery from "../assets/service_battery.jpg";
import serviceFuel from "../assets/service_fuel.jpg";
import serviceWash from "../assets/service_wash.jpg";

const servicesData = [
  {
    id: "towing",
    image: serviceTowing,
    number: "01",
    arTitle: "خدمة الونش والإنقاذ السريع",
    arDesc: "خدمة ونش وإنقاذ متكاملة على مدار الساعة لتأمين رحلتك. في TowHelp، نقوم بتوصيلك بأقرب مقدمي خدمات الونش الموثوقين لنقل سيارتك بأمان وسرعة مع تجربة سهلة وموثوقة من لحظة طلب الخدمة حتى وصول المساعدة إليك.",
    enTitle: "Towing & Rapid Rescue",
    enDesc: "A comprehensive 24/7 towing and roadside rescue service to keep your journey safe. At TowHelp, we connect you with the nearest trusted tow service providers to transport your car safely and swiftly, with a seamless and reliable experience from the moment you request the service until help reaches you.",
  },
  {
    id: "tire",
    image: serviceTire,
    number: "02",
    arTitle: "إصلاح وتغيير الإطارات المتنقل",
    arDesc: "لا تدع ثقب الإطار يعطل يومك. نصل إليك أينما كنت بأجهزة فك وتركيب هوائية محترفة لتغيير الإطار أو إصلاحه فوراً على الطريق. نقوم بمعايرة ضغط الهواء وفحص السلامة لضمان عودتك للقيادة بأمان ودون عناء.",
    enTitle: "Mobile Tire Repair & Replacement",
    enDesc: "Don't let a flat tire ruin your day. We come to you wherever you are, equipped with professional pneumatic tools to replace or repair your tire instantly on the road. We calibrate tire pressure and perform a safety check to ensure you get back to driving safely and hassle-free.",
  },
  {
    id: "battery",
    image: serviceBattery,
    number: "03",
    arTitle: "خدمة وصلة البطارية (Jump Start)",
    arDesc: "إذا تعطلت بطارية سيارتك فجأة ولم تتمكن من تشغيلها، يصل إليك فريق TowHelp بسرعة لتقديم خدمة وصلة البطارية (Jump Start) باحترافية، لتتمكن من استكمال رحلتك بأسرع وقت ممكن.",
    enTitle: "Battery Jump Start Service",
    enDesc: "If your car battery suddenly dies and you can't start it, the TowHelp team reaches you quickly to provide a professional Jump Start service, so you can resume your journey as fast as possible.",
  },
  {
    id: "fuel",
    image: serviceFuel,
    number: "04",
    arTitle: "توصيل الوقود في حالات الطوارئ",
    arDesc: "هل نفد منك الوقود أثناء الطريق؟ لا داعي للقلق! في TowHelp، بنوصلك بسرعة بكمية تصل إلى 4 لتر من الوقود لتتمكن من الوصول إلى أقرب محطة وقود واستكمال رحلتك بأمان ودون عناء.\n(تتوفر الخدمة وفقًا لنوع الوقود المناسب لسيارتك (92 أو 95))",
    enTitle: "Emergency Fuel Delivery",
    enDesc: "Ran out of fuel on the road? Don't worry! At TowHelp, we quickly deliver up to 4 liters of fuel so you can reach the nearest gas station and continue your journey safely and hassle-free.\n(Service is available with the fuel type suitable for your car — Octane 92 or 95)",
  },
  {
    id: "wash",
    image: serviceWash,
    number: "05",
    arTitle: "خدمات الغسيل والكار كير المتنقل",
    arDesc: "استمتع بخدمة غسيل وعناية متكاملة لسيارتك أمام منزلك أو مقر عملك، باستخدام معدات ومواد عالية الجودة تمنح سيارتك نظافة ولمعانًا احترافيًا دون الحاجة إلى إضاعة وقتك في الذهاب إلى مراكز الغسيل.",
    enTitle: "Mobile Washing & Car Care Services",
    enDesc: "Enjoy a complete washing and car care service right at your doorstep or office, using high-quality equipment and materials that give your car a professional clean and shine — without wasting your time going to car wash centers.",
  }
];

const ServicesListMobile = () => {
  const [services, setServices] = useState<any[]>([]);
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const currentLang = i18n.language.startsWith("ar") ? "ar" : "en";

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await getServices();
        setServices(response?.items || []);
      } catch (error) {
        console.error("Error fetching services:", error);
      }
    };
    fetchServices();
  }, []);

  // Map API services to local templates
  const displayServices = (services.length > 0 ? services : servicesData).map((s: any, idx: number) => {
    const template = servicesData[idx % servicesData.length];

    // Ensure our high-quality promotional descriptions and images are used consistently
    const title = currentLang === "ar" ? template.arTitle : template.enTitle;
    const desc = currentLang === "ar" ? template.arDesc : template.enDesc;

    return {
      id: s.id || template.id,
      number: template.number,
      image: template.image,
      title,
      desc,
    };
  });

  return (
    <Box
      id="services-mobile"
      sx={{
        py: 8,
        px: 2,
        background: "linear-gradient(180deg, #060606, #0e0e0e)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Mesh Background */}
      <Box
        className="bg-mesh-overlay"
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 0,
          pointerEvents: "none",
        }}
      />

      {/* Floating Glowing Blobs */}
      <Box
        sx={{
          position: "absolute",
          width: "280px",
          height: "280px",
          background: "radial-gradient(circle, rgba(255, 174, 0, 0.05) 0%, rgba(255, 174, 0, 0) 70%)",
          filter: "blur(50px)",
          top: "15%",
          left: "5%",
          zIndex: 0,
          pointerEvents: "none",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          width: "280px",
          height: "280px",
          background: "radial-gradient(circle, rgba(255, 174, 0, 0.04) 0%, rgba(255, 174, 0, 0) 70%)",
          filter: "blur(50px)",
          bottom: "15%",
          right: "5%",
          zIndex: 0,
          pointerEvents: "none",
        }}
      />

      {/* Title */}
      <motion.div
        initial={{ opacity: 0, y: -25 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        style={{ position: "relative", zIndex: 2 }}
      >
        <Typography
          variant="h4"
          textAlign="center"
          fontWeight={900}
          mb={6}
          sx={{
            fontFamily: "Poppins, Ozen",
            background: "linear-gradient(135deg, #FFAE00 0%, #FF9100 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            textTransform: "uppercase",
            textShadow: "0 0 25px rgba(255, 174, 0, 0.1)",
          }}
        >
          {t("whatWeProvide")}
        </Typography>
      </motion.div>

      {/* List of full-width premium cards */}
      <Box
        sx={{
          position: "relative",
          zIndex: 2,
          display: "flex",
          flexDirection: "column",
          gap: 4,
          maxWidth: "600px",
          mx: "auto",
          mb: 6,
        }}
      >
        {displayServices.map((service, index) => (
          <Paper
            component={motion.div}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 0.6, delay: index * 0.05 }}
            key={service.id}
            elevation={0}
            sx={{
              borderRadius: "20px",
              overflow: "hidden",
              border: "1.5px solid rgba(255, 174, 0, 0.12)",
              background: "#0c0c0c",
              boxShadow: "0 15px 35px rgba(0,0,0,0.5)",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Image Header with Overlay */}
            <Box sx={{ width: "100%", height: "180px", position: "relative" }}>
              <Box
                component="img"
                src={service.image}
                alt={service.title}
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: "linear-gradient(180deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.85) 100%)",
                }}
              />
              {/* Floating golden progress number */}
              <Box
                sx={{
                  position: "absolute",
                  top: "15px",
                  [isRTL ? "left" : "right"]: "15px",
                  background: "rgba(255, 174, 0, 0.95)",
                  color: "#000",
                  fontFamily: "Poppins",
                  fontWeight: 900,
                  px: 2,
                  py: 0.5,
                  borderRadius: "20px",
                  fontSize: "0.85rem",
                  boxShadow: "0 4px 10px rgba(255,174,0,0.3)",
                }}
              >
                {service.number}
              </Box>
            </Box>

            {/* Content body */}
            <Box dir={isRTL ? "rtl" : "ltr"} sx={{ p: 3, textAlign: isRTL ? "right" : "left" }}>
              <Typography
                variant="h5"
                fontWeight={850}
                mb={1.5}
                sx={{
                  fontFamily: "Poppins, Ozen",
                  color: "#FFAE00",
                }}
              >
                {service.title}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "#cccccc",
                  fontFamily: "Poppins, Ozen",
                  lineHeight: 1.6,
                  fontSize: "0.88rem",
                  mb: 3,
                }}
              >
                {service.desc}
              </Typography>

              {/* Book button for each card */}
              <Button
                component="a"
                href="#booking-section"
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById("booking-section")?.scrollIntoView({ behavior: "smooth" });
                }}
                fullWidth
                sx={{
                  borderRadius: "12px",
                  py: 1,
                  background: "rgba(255, 174, 0, 0.08)",
                  border: "1.5px solid rgba(255, 174, 0, 0.25)",
                  color: "#FFAE00",
                  fontWeight: "bold",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    background: "rgba(255, 174, 0, 0.15)",
                  },
                }}
              >
                {currentLang === "ar" ? "احجز الخدمة الآن" : "Book Service Now"}
              </Button>
            </Box>
          </Paper>
        ))}
      </Box>

      {/* Booking Form mobile slide in */}
      <motion.div
        id="booking-section"
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.1 }}
        transition={{ duration: 0.8 }}
        style={{ position: "relative", zIndex: 2 }}
      >
        <BookingForm />
      </motion.div>
    </Box>
  );
};

export default ServicesListMobile;
